import React from 'react';
import { Car, Bike, Info } from 'lucide-react';
import { VehicleInfo } from './types';

interface VehicleInfoStepProps {
  vehicleType: 'auto' | 'motorcycle';
  vehicleInfo: VehicleInfo;
  setVehicleInfo: (info: VehicleInfo) => void;
  raceNumberError: string | null;
}

export default function VehicleInfoStep({
  vehicleType,
  vehicleInfo,
  setVehicleInfo,
  raceNumberError
}: VehicleInfoStepProps) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        {vehicleType === 'auto' ? (
          <Car className="h-8 w-8 text-primary-500" />
        ) : (
          <Bike className="h-8 w-8 text-primary-500" />
        )}
        <h3 className="text-xl font-semibold text-white">
          {vehicleType === 'auto' ? 'Auto' : 'Motorcycle'} Vehicle Information
        </h3>
      </div>

      <div className="bg-blue-900/20 border border-blue-800/30 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-blue-300 text-sm font-medium">About This Section</p>
            <p className="text-blue-200 text-sm mt-1">
              This section is for the <strong>vehicle information</strong>. Please provide details about the vehicle that will be used for racing.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {vehicleType === 'motorcycle' ? 'Vehicle Number (License Plate)' : 'Vehicle Number'} *
          </label>
          <input
            type="text"
            value={vehicleInfo.vehicleNumber}
            onChange={(e) => setVehicleInfo({ ...vehicleInfo, vehicleNumber: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Race Number *
          </label>
          <input
            type="text"
            value={vehicleInfo.raceNumber}
            onChange={(e) => setVehicleInfo({ ...vehicleInfo, raceNumber: e.target.value })}
            className={`w-full bg-zinc-800 border ${raceNumberError ? 'border-primary-500' : 'border-zinc-700'} rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none`}
            placeholder="e.g., 7, 07, 123"
            required
          />

          {raceNumberError && (
            <p className="text-primary-500 text-sm mt-1">{raceNumberError}</p>
          )}
          <p className="text-xs text-gray-400 mt-1">
            Must be unique. "7" and "07" are different numbers.
          </p>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Vehicle Brand & Model *
          </label>
          <input
            type="text"
            value={vehicleInfo.vehicleBrandModel}
            onChange={(e) => setVehicleInfo({ ...vehicleInfo, vehicleBrandModel: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
            placeholder="e.g., Honda S2000"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Color
          </label>
          <input
            type="text"
            value={vehicleInfo.color}
            onChange={(e) => setVehicleInfo({ ...vehicleInfo, color: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Engine Brand
          </label>
          <input
            type="text"
            value={vehicleInfo.engineBrand}
            onChange={(e) => setVehicleInfo({ ...vehicleInfo, engineBrand: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Year *
          </label>
          <input
            type="number"
            min="1900"
            max={new Date().getFullYear() + 1}
            value={vehicleInfo.year}
            onChange={(e) => setVehicleInfo({ ...vehicleInfo, year: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Number of Cylinders
          </label>
          <input
            type="number"
            min="1"
            max="16"
            value={vehicleInfo.numberOfCylinders}
            onChange={(e) => setVehicleInfo({ ...vehicleInfo, numberOfCylinders: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
          />
        </div>
      </div>
    </div>
  );
} 